import { useEffect, useState, useMemo } from "react";
import { PlusCircleOutlined, MoreOutlined } from "@ant-design/icons";
import { Spin, Modal, message, Button, Dropdown } from "antd";
import TableComponent from "../../components/TableComponent";
import { API_ENDPOINTS } from "../../utils/constants";
import { axiosInstance } from "../../utils/axiosInstance";
import { createColumn } from "../../utils/tableUtils";
import { useNavigate } from "react-router-dom";
import MainButtonComponent from "../../components/MainButtonComponent";
import {
	HeaderComponent,
	Container,
	ContainerHeader,
	ActionButtonComponent,
	StatusCell
} from "./styles";
import { AxiosResponse } from "axios";
import { useMobileAppVersionContext } from "../../context/MobileAppVersionContext/MobileAppVersionContext";
import { APP_OPTIONS } from "./constants";

interface MobileAppVersion {
	versionId: string;
	platform: string;
	version: string;
	appId: number;
	hasWarning: boolean;
	forceUpdate: boolean;
}
interface MobileAppVersionListApiResponse {
	result: {
		items: MobileAppVersion[];
	};
	error: string | null;
	isSuccessful: boolean;
}

const MobileAppVersionList = () => {
	const [tableData, setTableData] = useState<any[]>([]);
	const [pageLoading, setPageLoading] = useState(true);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [currentItem, setCurrentItem] = useState<any>(null);
	const { clearMobileAppVersionData } = useMobileAppVersionContext();

	const navigate = useNavigate();

	const getAppLabel = (appValue: number | string) => {
		const app = APP_OPTIONS.find(a => a.value === appValue);
		return app?.label || String(appValue);
	};

	const getMobileAppVersionList = async () => {
		setPageLoading(true);
		try {
			const response: AxiosResponse<MobileAppVersionListApiResponse> = await axiosInstance.get(
				API_ENDPOINTS.GET_MOBILE_APP_VERSION_LIST
			);

			setTableData(response?.data?.result?.items);
		} catch (error) {
			return [[], false];
		} finally {
			setPageLoading(false);
		}
	};

	const handleDelete = async (versionId: string) => {
		try {
			await axiosInstance.post(API_ENDPOINTS.REMOVE_MOBILE_APP_VERSION, { versionId : versionId });
			message.success("Mobil Uygulama Sürümü başarıyla silindi.");
			getMobileAppVersionList();
		} catch (error) {
			message.error("Mobil Uygulama Sürümü silinirken bir hata oluştu.");
		} finally {
			setIsModalVisible(false);
		}
	};

	useEffect(() => {
		getMobileAppVersionList();
		clearMobileAppVersionData();
	}, []);


	const columns = useMemo(() => [
		createColumn({
			title: "Platform",
			dataIndex: "platform",
			width: 30,
			sorterType: "string",
		}),
		createColumn({
			title: "Sürüm",
			dataIndex: "version",
			width: 50,
			sorterType: "string",
		}),
		createColumn({
			title: "Uyarı",
			dataIndex: "hasWarning",
			width: 50,
			sorterType: "boolean",
			render: (text, record) => (
				<StatusCell $isVisible={!record.hasWarning}>{!record.hasWarning ? "Normal" : "Uyarı"}</StatusCell>
			),
		}),
		createColumn({
			title: "Force Update",
			dataIndex: "forceUpdate",
			width: 50,
			sorterType: "boolean",
			render: (text, record) => (
				<StatusCell $isVisible={!record.forceUpdate}>{!record.forceUpdate ? "Normal" : "Zorunlu"}</StatusCell>
			),
		}),
		createColumn({
			title: "Uygulama Adı",
			dataIndex: "appId",
			width: 50,
			sorterType: "string",
			render: (text) => getAppLabel(text),
		}),
		createColumn({
			title: "",
			dataIndex: "action",
			width: 20,
			render: (text, record: any) => {
				const menuItems = [
					{
						key: "edit",
						label: "Güncelle",
						onClick: () => navigate(`/edit-mobile-app-version/${record.versionId}`),
					},
					{
						key: "delete",
						label: "Sil",
						onClick: () => {
							setCurrentItem(record);
							setIsModalVisible(true);
						},
					},
				];

				return (
					<Dropdown menu={{ items: menuItems }} trigger={["click"]}>
						<MoreOutlined />
					</Dropdown>
				);
			},
		}),
	], [tableData]);


	return (
		<div>
			<HeaderComponent>Mobil Uygulama Sürümleri</HeaderComponent>
			<Container>
				<Spin spinning={pageLoading}>
					<ContainerHeader>
						<ActionButtonComponent>
							<MainButtonComponent
								style={{ marginRight: "15px" }}
								icon={<PlusCircleOutlined />}
								onClick={() => navigate("/add-mobile-app-version")}
							>
								Yeni Ekle
							</MainButtonComponent>
						</ActionButtonComponent>
					</ContainerHeader>
				</Spin>
				{!pageLoading && (
					<TableComponent
						columns={columns}
						dataSource={tableData}
					/>
				)}
			</Container>

			<Modal
				title="Mobil Uygulama Sürümünü Sil"
				open={isModalVisible}
				onCancel={() => setIsModalVisible(false)}
				footer={[
					<Button key="back" type="text" onClick={() => setIsModalVisible(false)}>
						İptal
					</Button>,
					<Button
						key="submit"
						type="primary"
						danger
						onClick={() => handleDelete(currentItem?.versionId)}
					>
						Evet
					</Button>,
				]}
			>
				<p><strong>{`"${currentItem?.platform} v${currentItem?.version}"`}</strong> sürümünü silmek istediğinizden emin misiniz?</p>
			</Modal>
		</div>
	);
};

export default MobileAppVersionList;
